import { Spinner } from "@npco/zeller-design-system";
import * as Illustration from "@npco/zeller-design-system/illustration";
import { ADT } from "pb.adt";

import { formatCurrency } from "../../../../@lib-i18n";
import { Layout } from "../../shared/layout";
import { createOperationPages } from "../../shared/operation-pages";
import { PageTemplate } from "../../shared/page-template";
import { Prompt } from "../../shared/prompt";
import { Signature } from "../../shared/signature";
import type { Page } from "../refund-matched/page";
import { RefundTransaction } from "./refund-transaction";
import { SelectTransaction } from "./select-transaction";

export const defaultLayoutProps = {
	actionsStyle: "full-width",
	logo: true,
} satisfies Layout.Props;

/** Shared with `closeWithTransactionNotFound`, so the host is told the same. */
export const transactionNotFoundCopy = {
	title: "Transaction Not Found",
	message: [
		"This transaction could not be found in Zeller.",
		"Please contact Zeller Support if this keeps happening.",
	],
};

export function render(page: Page) {
	return ADT.switch(page, {
		...createOperationPages("Refund"),
		InitialisingRefund: PageTemplate({
			image: <Spinner size="lg" />,
			title: "Initialising Refund",
			intermediate: true,
		}),
		AwaitTransaction: PageTemplate({
			image: <Spinner size="lg" />,
			title: "Locating Transaction",
			intermediate: true,
		}),
		SignatureSubmitted: ({
			signature,
			onClose,
			onApprove,
			onDecline,
			cancelling,
		}) => (
			<Layout
				{...defaultLayoutProps}
				onClose={onClose}
				pending={cancelling ? "close" : undefined}
				actions={[
					{ label: "Approve", onClick: onApprove, primary: true },
					{ label: "Decline", onClick: onDecline, primary: false },
				]}
			>
				<Prompt
					image={<Signature signature={signature} />}
					title="Verify Signature"
				/>
			</Layout>
		),
		// response states
		Approved: ({ currency, totalCents, onDone }) => (
			<Layout
				{...defaultLayoutProps}
				timeout={{ onTimeout: onDone }}
				actions={[{ label: "Done", onClick: onDone, primary: true }]}
			>
				<Prompt
					image={<Spinner size="lg" result="ok" />}
					title={`${formatCurrency({ amountInCents: totalCents, currency })} Refunded`}
				/>
			</Layout>
		),
		Declined: ({ title, subtitle, message, onRetry, onCancel }) => (
			<Layout
				{...defaultLayoutProps}
				timeout={{ onTimeout: onCancel }}
				actions={[
					{ label: "Try Again", onClick: onRetry, primary: true },
					{ label: "Cancel", onClick: onCancel },
				]}
			>
				<Prompt
					image={<Spinner size="lg" result="error" />}
					title={title}
					subtitle={subtitle}
					message={message}
				/>
			</Layout>
		),
		SelectTransaction: (props) => <SelectTransaction {...props} />,
		RefundTransaction: (props) => <RefundTransaction {...props} />,
		// Matched-only page; kept hand-rolled because PageTemplate would add an
		// auto-dismiss timeout this page deliberately does without.
		ErrorTransactionNotFound: ({ onClose }) => (
			<Layout
				{...defaultLayoutProps}
				actions={[{ label: "Close", onClick: onClose, primary: true }]}
			>
				<Prompt
					image={<Illustration.ErrorQuestionMark />}
					{...transactionNotFoundCopy}
				/>
			</Layout>
		),
	});
}
